import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldAlert, Loader2 } from "lucide-react";
import AuthEventsPanel from "@/components/audit/AuthEventsPanel";

const ACTION_COLOUR = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-sky-100 text-sky-800",
  delete: "bg-rose-100 text-rose-800",
  approve: "bg-violet-100 text-violet-800",
  export: "bg-amber-100 text-amber-800",
  view: "bg-slate-100 text-slate-700",
};

export default function AuditLogPage() {
  const { can, loaded } = usePermissions();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");

  useEffect(() => {
    if (!loaded || !can("org_mgmt", "view")) return;
    const load = async () => {
      setBusy(true);
      try {
        const params = new URLSearchParams();
        if (module) params.set("module", module);
        if (action) params.set("action", action);
        params.set("limit", "200");
        const [{ data: rows }, { data: sum }] = await Promise.all([
          api.get(`/audit/logs?${params.toString()}`),
          api.get(`/audit/logs/summary`),
        ]);
        setLogs(rows);
        setSummary(sum);
      } finally { setBusy(false); }
    };
    load();
    /* eslint-disable-next-line */
  }, [loaded, module, action]);

  if (!loaded) return <AppLayout title="Audit Log"><div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" /></AppLayout>;

  if (!can("org_mgmt", "view")) {
    return (
      <AppLayout title="Audit Log">
        <div className="max-w-md mx-auto mt-16 text-center">
          <div className="w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-rose-700" />
          </div>
          <h2 className="font-display text-xl font-semibold text-slate-900">Access restricted</h2>
          <p className="text-slate-600 mt-2 text-sm">Only administrators can view the audit log.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Audit Log" subtitle="Immutable record of every action taken across the platform">
      <Tabs defaultValue="app" className="w-full">
        <TabsList data-testid="audit-tabs" className="mb-4">
          <TabsTrigger value="app" data-testid="audit-tab-app">Application</TabsTrigger>
          <TabsTrigger value="auth" data-testid="audit-tab-auth">Authentication</TabsTrigger>
        </TabsList>
        <TabsContent value="app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Total entries</div>
          <div className="mt-1 font-display text-2xl text-slate-900">{summary?.total ?? "—"}</div>
        </div>
        {(summary?.by_action || []).slice(0, 3).map((a) => (
          <div key={a.action} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">{a.action}</div>
            <div className="mt-1 font-display text-2xl text-slate-900">{a.count}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <Input value={module} onChange={(e) => setModule(e.target.value)} placeholder="Filter by module (e.g. crm)" className="max-w-xs bg-white" />
        <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action (e.g. create)" className="max-w-xs bg-white" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
          <div className="col-span-2">Time</div>
          <div className="col-span-2">User</div>
          <div className="col-span-1">Action</div>
          <div className="col-span-2">Module</div>
          <div className="col-span-2">Resource</div>
          <div className="col-span-3">Detail</div>
        </div>
        <div className="divide-y divide-slate-100">
          {busy && <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>}
          {!busy && logs.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">No audit entries yet.</div>
          )}
          {logs.map((r) => (
            <div key={r.id} data-testid={TID.auditRow(r.id)} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm text-slate-700 items-center">
              <div className="col-span-2 text-xs text-slate-500 truncate">{r.timestamp?.slice(0, 19).replace("T", " ")}</div>
              <div className="col-span-2 truncate">{r.user_email}</div>
              <div className="col-span-1">
                <Badge className={ACTION_COLOUR[r.action] || "bg-slate-100 text-slate-700"}>{r.action}</Badge>
              </div>
              <div className="col-span-2 truncate">{r.module}</div>
              <div className="col-span-2 truncate">{r.resource_type}{r.resource_id ? ` · ${r.resource_id.slice(-6)}` : ""}</div>
              <div className="col-span-3 truncate text-xs text-slate-500">{r.detail || "—"}</div>
            </div>
          ))}
        </div>
      </div>
        </TabsContent>
        <TabsContent value="auth">
          <AuthEventsPanel />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
